import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
  IconButton,
  Checkbox,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Pagination from "../../../Pagination";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
} from "../../../../Styles/Styles";

const SearchesManagement = () => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1); // Custom pagination starts from 1
  const [rowsPerPage] = useState(10); // Rows per page (fixed for now)
  const [totalPages, setTotalPages] = useState(0);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/search/allsearches?page=${currentPage}&limit=${rowsPerPage}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setSearches(data.searches);
      setTotalPages(data.totalPages); // Set the total pages from the API response
    } catch (error) {
      console.error("Error fetching search logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingChange = async (searchId, trending) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/search/updatetrending`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ searchId, trending }),
        }
      );
      setSearches((prevSearches) =>
        prevSearches.map((search) =>
          search._id === searchId ? { ...search, trending } : search
        )
      );
    } catch (error) {
      console.error("Error updating trending status:", error);
    }
  };

  const handleDeleteSearch = async (searchId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/search/deletesearch`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ searchId }),
        }
      );
      setSearches((prevSearches) =>
        prevSearches.filter((search) => search._id !== searchId)
      );
    } catch (error) {
      console.error("Error deleting search:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage); // Update current page
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Searches Management - Listed.ky</title>
        <meta name="description" content="Searches Management - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Searches Management
      </Typography>
      <TableContainer sx={{ ...cardOutline }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Term</TableCell>
              <TableCell sx={{ ...headingFonts }}>Count</TableCell>
              <TableCell sx={{ ...headingFonts }}>Trending</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searches.map((search) => (
              <TableRow key={search._id}>
                <TableCell sx={{ ...headingFonts }}>{search.term}</TableCell>
                <TableCell sx={{ ...fonts }}>{search.count}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={search.trending || false}
                    onChange={(e) =>
                      handleTrendingChange(search._id, e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteSearch(search._id)}
                    sx={{ ...noOutline }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange} // Custom pagination expects only page number
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchesManagement;
